import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Color, ColorV2 } from "config/colors";
import { FontSize } from "config/font";
import { Text as TextV2 } from "components/designV2/text/text";
import { Dropdown } from "components/designV2/dropdown/dropdown";
import { InputField } from "components/designV2/inputfield/inputfield";
import { TooltipHover } from "components/designV2/tooltip/tooltip";
import {
  CostDivider,
  CostFootnote,
  CostLabel,
  CostRow,
  CostRows,
  CostValue,
  DashText,
  EstimatedCostLabel,
  EstimatedCostRow,
  ErrorTextWrapper,
  EmptyCostText,
  FeesContainer,
  InfoCircle,
  HourlyRateInputContainer,
  HourlyRateInputsRow,
  HourlyRateInputsWrapper,
  HourlyRateTopRow,
  MaxRateFieldWrapper,
  MinRateFieldWrapper,
  PaymentMethodWrapper,
  RowWrapper,
} from "./hourlyRate.styled";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import { CompanyGigShift, GigCosts, GigType, PaymentMethod } from "model/Gig";
import { getCosts } from "web-apps/company/utils/utils";
import { parseGigHourlyRateInput } from "../logic/gigForm.rate.logic";

type Props = {
  minHourlyRate: number;
  maxHourlyRate: number;
  onChange: (min: number, max: number) => void;
  onPaymentTypeChange: (paymentType: PaymentMethod) => void;
  hours: number;
  shifts: CompanyGigShift[];
  gigType: GigType | undefined;
  paymentType: PaymentMethod | undefined;
};

const emptyCosts = {
  grossSalary: 0,
  obAmount: 0,
  socialFee: 0,
  simflexityFee: 0,
  totalCost: 0,
  vacationPayAmount: 0,
};

const isPaymentMethod = (value: string): value is PaymentMethod => {
  return value === PaymentMethod.SIMFLEXITY_PAY || value === PaymentMethod.NONE;
};

export const HourlyRate: React.FC<Props> = ({
  minHourlyRate,
  maxHourlyRate,
  onChange,
  onPaymentTypeChange,
  hours,
  shifts,
  gigType,
  paymentType,
}) => {
  const { t } = useTranslation();
  const [companyState] = useCompanyStore();
  const normalizedPaymentType = paymentType ?? PaymentMethod.NONE;
  const [gigMinCost, setGigMinCost] = useState<GigCosts>(emptyCosts);
  const [gigMaxCost, setGigMaxCost] = useState<GigCosts>(emptyCosts);
  const [hasCostCalculationError, setHasCostCalculationError] = useState(false);
  const [minRateInput, setMinRateInput] = useState<string>(
    minHourlyRate.toString(),
  );
  const [maxRateInput, setMaxRateInput] = useState<string>(
    maxHourlyRate.toString(),
  );
  const [isEditingMinRate, setIsEditingMinRate] = useState(false);
  const [isEditingMaxRate, setIsEditingMaxRate] = useState(false);
  const totalDeduction = shifts.reduce(
    (sum, shift) =>
      sum + (shift.deductionAmount !== undefined ? shift.deductionAmount : 0),
    0,
  );

  const isValidHourlyRate =
    minHourlyRate > 0 && maxHourlyRate > 0 && maxHourlyRate >= minHourlyRate;

  useEffect(() => {
    if (!isEditingMinRate) {
      setMinRateInput(minHourlyRate.toString());
    }
  }, [minHourlyRate, isEditingMinRate]);

  useEffect(() => {
    if (!isEditingMaxRate) {
      setMaxRateInput(maxHourlyRate.toString());
    }
  }, [maxHourlyRate, isEditingMaxRate]);

  useEffect(() => {
    const companyId = companyState.company?.id;
    if (!companyId || !isValidHourlyRate) {
      setGigMinCost(emptyCosts);
      setGigMaxCost(emptyCosts);
      setHasCostCalculationError(false);
      return;
    }

    let isActive = true;

    const updateCosts = async (): Promise<void> => {
      try {
        const costs = await getCosts(
          shifts,
          minHourlyRate,
          maxHourlyRate,
          gigType,
          companyId,
          normalizedPaymentType,
        );

        if (!isActive) {
          return;
        }

        setGigMinCost(costs.minCost);
        setGigMaxCost(costs.maxCost);
        setHasCostCalculationError(false);
      } catch (_error: unknown) {
        if (!isActive) {
          return;
        }

        setGigMinCost(emptyCosts);
        setGigMaxCost(emptyCosts);
        setHasCostCalculationError(true);
      }
    };

    void updateCosts();

    return () => {
      isActive = false;
    };
  }, [
    minHourlyRate,
    maxHourlyRate,
    shifts,
    normalizedPaymentType,
    gigType,
    companyState.company?.id,
    isValidHourlyRate,
  ]);

  const emptyCostText = t("GigsCompany.AddShiftToSeeEstimatedCostShift", {
    defaultValue: t("GigsCompany.AddShiftToSeeEstimatedCost"),
  });

  const handlePaymentTypeChange = (value: string): void => {
    if (!isPaymentMethod(value)) {
      return;
    }

    onPaymentTypeChange(value);
  };

  return (
    <HourlyRateInputContainer>
      <HourlyRateTopRow>
        <HourlyRateInputsWrapper>
          <HourlyRateInputsRow>
            <MinRateFieldWrapper>
              <InputField
                label={`${t("GigsCompany.SelectASpan")} (kr/h)`}
                value={minRateInput}
                onChange={(value: string) => {
                  setMinRateInput(value);
                  if (value.trim() === "") {
                    onChange(0, maxHourlyRate);
                    return;
                  }
                  onChange(
                    parseGigHourlyRateInput(value, minHourlyRate),
                    maxHourlyRate,
                  );
                }}
                onFocus={() => setIsEditingMinRate(true)}
                onBlur={() => setIsEditingMinRate(false)}
                onClear={() => {
                  setMinRateInput("");
                  onChange(0, maxHourlyRate);
                }}
                placeholder={t("CreateShift.minRate")}
                type="number"
                fullWidth
              />
            </MinRateFieldWrapper>
            <DashText color={Color.SeaBlue600} fontSize={FontSize.H4}>
              -
            </DashText>
            <MaxRateFieldWrapper>
              <InputField
                value={maxRateInput}
                onChange={(value: string) => {
                  setMaxRateInput(value);
                  if (value.trim() === "") {
                    onChange(minHourlyRate, 0);
                    return;
                  }
                  onChange(
                    minHourlyRate,
                    parseGigHourlyRateInput(value, maxHourlyRate),
                  );
                }}
                onFocus={() => setIsEditingMaxRate(true)}
                onBlur={() => setIsEditingMaxRate(false)}
                onClear={() => {
                  setMaxRateInput("");
                  onChange(minHourlyRate, 0);
                }}
                placeholder={t("CreateShift.maxRate")}
                type="number"
                fullWidth
              />
            </MaxRateFieldWrapper>
          </HourlyRateInputsRow>
        </HourlyRateInputsWrapper>

        <PaymentMethodWrapper>
          <Dropdown
            label={t("GigsCompany.PaymentMethod")}
            options={[
              {
                value: PaymentMethod.SIMFLEXITY_PAY,
                label: t("SimflexityPay.SimflexityPay"),
              },
              {
                value: PaymentMethod.NONE,
                label: t("GigsCompany.OwnSalaryProcessing"),
              },
            ]}
            value={normalizedPaymentType}
            onChange={handlePaymentTypeChange}
            fullWidth
          />
        </PaymentMethodWrapper>
      </HourlyRateTopRow>

      {!isValidHourlyRate && (
        <ErrorTextWrapper>
          <TextV2 typography="s1" color={ColorV2.NegativeRed}>
            {t("GigsCompany.MaxRateMustBeHigherThanMinRate")}
          </TextV2>
        </ErrorTextWrapper>
      )}
      {hours > 0 && hasCostCalculationError && (
        <ErrorTextWrapper>
          <TextV2 typography="s1" color={ColorV2.NegativeRed}>
            {t("GigsCompany.UnableToCalculateCost", {
              defaultValue: "Kunde inte berakna kostnad just nu.",
            })}
          </TextV2>
        </ErrorTextWrapper>
      )}

      <EstimatedCostRow>
        <EstimatedCostLabel>
          {t("GigsCompany.EstimatedCost")}
        </EstimatedCostLabel>
        <TooltipHover text={t("GigsCompany.FeeInfo")} arrow="down">
          <InfoCircle>i</InfoCircle>
        </TooltipHover>
      </EstimatedCostRow>
      <RowWrapper>
        <FeesContainer>
          {hours ? (
            <CostRows>
              <CostRow>
                <CostLabel>{t("GigsCompany.GrossSalary")}</CostLabel>
                <CostValue>
                  {gigMinCost.grossSalary.toFixed(2)} -{" "}
                  {gigMaxCost.grossSalary.toFixed(2)} kr
                </CostValue>
              </CostRow>
              <CostDivider />

              <CostRow>
                <CostLabel>{t("ProfileCompany.OB")}</CostLabel>
                <CostValue>
                  {gigMinCost.obAmount.toFixed(2)} -{" "}
                  {gigMaxCost.obAmount.toFixed(2)} kr
                </CostValue>
              </CostRow>
              <CostDivider />

              <CostRow>
                <CostLabel>{t("GigsCompany.SocialFees")}</CostLabel>
                <CostValue>
                  {gigMinCost.socialFee.toFixed(2)} -{" "}
                  {gigMaxCost.socialFee.toFixed(2)} kr
                </CostValue>
              </CostRow>
              <CostDivider />

              {normalizedPaymentType === PaymentMethod.SIMFLEXITY_PAY && (
                <>
                  <CostRow>
                    <CostLabel>{t("GigsCompany.SimflexityFee")}</CostLabel>
                    <CostValue>
                      {gigMinCost.simflexityFee.toFixed(2)} -{" "}
                      {gigMaxCost.simflexityFee.toFixed(2)} kr
                    </CostValue>
                  </CostRow>
                  <CostDivider />
                </>
              )}

              <CostRow>
                <CostLabel>{t("GigsCompany.VacationPayAmount")}</CostLabel>
                <CostValue>
                  {gigMinCost.vacationPayAmount.toFixed(2)} -{" "}
                  {gigMaxCost.vacationPayAmount.toFixed(2)} kr
                </CostValue>
              </CostRow>
              <CostDivider />

              {totalDeduction !== 0 && (
                <>
                  <CostRow>
                    <CostLabel>{t("GigsCompany.Deduction")}:</CostLabel>
                    <CostValue>{totalDeduction.toFixed(2)} kr</CostValue>
                  </CostRow>
                  <CostDivider />
                </>
              )}

              <CostRow>
                <CostLabel>{t("GigsCompany.TotalCost")}</CostLabel>
                <CostValue>
                  {gigMinCost.totalCost.toFixed(2)} -{" "}
                  {gigMaxCost.totalCost.toFixed(2)} kr
                </CostValue>
              </CostRow>

              <CostFootnote>{t("GigsCompany.FinalFee")}</CostFootnote>
            </CostRows>
          ) : (
            <EmptyCostText>{emptyCostText}</EmptyCostText>
          )}
        </FeesContainer>
      </RowWrapper>
    </HourlyRateInputContainer>
  );
};
